import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_URL } from '../config';

// ---------------------------------------------------------------------------
// API helper — all calls go through the centralized API_URL (see src/config.js)
// ---------------------------------------------------------------------------
const API = API_URL;

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch(path, options = {}, token = null) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { ...authHeaders(token), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'API error');
  }
  return res.json();
}

const generateId = () => Math.random().toString(36).substring(2, 15);

// ---------------------------------------------------------------------------
// Platform metadata (display — OAuth is handled by FastAPI backend)
// ---------------------------------------------------------------------------
const PLATFORM_META = [
  { id: 'tiktok',    name: 'TikTok',    icon: 'music',     color: '#000000', supported: true },
  { id: 'instagram', name: 'Instagram', icon: 'instagram', color: '#E4405F', supported: true },
  { id: 'youtube',   name: 'YouTube',   icon: 'youtube',   color: '#FF0000', supported: true },
  { id: 'linkedin',  name: 'LinkedIn',  icon: 'linkedin',  color: '#0A66C2', supported: true },
  { id: 'twitter',   name: 'Twitter/X', icon: 'twitter',   color: '#1DA1F2', supported: true },
  { id: 'facebook',  name: 'Facebook',  icon: 'facebook',  color: '#1877F2', supported: true },
  { id: 'pinterest', name: 'Pinterest', icon: 'pin',       color: '#E60023', supported: true },
  { id: 'threads',   name: 'Threads',   icon: 'at-sign',   color: '#000000', supported: true },
];

// ---------------------------------------------------------------------------
// Auth Store — real JWT login via FLARE /auth/token
// ---------------------------------------------------------------------------
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          // FLARE /auth/token expects JSON {email, password}
          const data = await apiFetch('/auth/token', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
          });
          const token = data.access_token;
          const profile = await apiFetch('/auth/me', {}, token);
          const user = {
            id: profile.id,
            email: profile.email,
            name: profile.full_name || profile.email.split('@')[0],
            plan: profile.plan || 'free',
            avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${profile.email}`,
            createdAt: profile.created_at,
          };
          set({ user, token, refreshToken: data.refresh_token, isAuthenticated: true, isLoading: false });
          return { success: true };
        } catch (e) {
          set({ isLoading: false });
          return { success: false, error: e.message };
        }
      },

      signup: async (email, password, name) => {
        set({ isLoading: true });
        try {
          await apiFetch('/auth/register', {
            method: 'POST',
            body: JSON.stringify({ email, password, full_name: name || email.split('@')[0] }),
          });
          const result = await get().login(email, password);
          set({ isLoading: false });
          return result;
        } catch (e) {
          set({ isLoading: false });
          return { success: false, error: e.message };
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        try {
          if (refreshToken) {
            await apiFetch('/auth/logout', {
              method: 'POST',
              body: JSON.stringify({ refresh_token: refreshToken }),
            });
          }
        } catch (_) {}
        set({ user: null, token: null, refreshToken: null, isAuthenticated: false });
      },

      updateUser: async (updates) => {
        const { token, user } = get();
        try {
          const updated = await apiFetch('/auth/me', { method: 'PUT', body: JSON.stringify(updates) }, token);
          set({ user: { ...user, ...updated } });
          return { success: true };
        } catch (e) {
          return { success: false, error: e.message };
        }
      },
    }),
    {
      name: 'flare-auth',
      partialize: (s) => ({ user: s.user, token: s.token, refreshToken: s.refreshToken, isAuthenticated: s.isAuthenticated }),
    }
  )
);

// ---------------------------------------------------------------------------
// Platforms Store — real accounts from /publish/accounts
// ---------------------------------------------------------------------------
export const usePlatformsStore = create(
  persist(
    (set, get) => ({
      platforms: PLATFORM_META.map(p => ({ ...p, connected: false })),

      fetchPlatforms: async () => {
        const { token } = useAuthStore.getState();
        if (!token) return;
        try {
          const data = await apiFetch('/publish/accounts', {}, token);
          const connected = new Set((data.accounts || []).map(a => a.platform));
          set(state => ({
            platforms: state.platforms.map(p => ({
              ...p,
              connected: connected.has(p.id),
              handle: (data.accounts || []).find(a => a.platform === p.id)?.handle,
            })),
          }));
        } catch (_) {}
      },

      connectPlatform: async (platformId) => {
        const { token } = useAuthStore.getState();
        try {
          const data = await apiFetch(`/publish/oauth/${platformId}/url`, {}, token);
          if (data.url) window.open(data.url, '_blank');
          return { success: true, oauthUrl: data.url };
        } catch (e) {
          return { success: false, error: e.message };
        }
      },

      disconnectPlatform: async (platformId) => {
        const { token } = useAuthStore.getState();
        try {
          await apiFetch(`/publish/accounts/${platformId}`, { method: 'DELETE' }, token);
          set(state => ({
            platforms: state.platforms.map(p =>
              p.id === platformId ? { ...p, connected: false, handle: null } : p
            ),
          }));
          return { success: true };
        } catch (e) {
          return { success: false, error: e.message };
        }
      },

      getConnectedPlatforms: () => get().platforms.filter(p => p.connected),
    }),
    { name: 'flare-platforms' }
  )
);

// ---------------------------------------------------------------------------
// Posts Store — backed by /calendar endpoint
// ---------------------------------------------------------------------------
export const usePostsStore = create(
  persist(
    (set, get) => ({
      posts: [],
      isLoading: false,

      fetchPosts: async () => {
        const { token } = useAuthStore.getState();
        if (!token) return;
        try {
          const data = await apiFetch('/calendar', {}, token);
          const posts = (data.items || []).map(item => ({
            id: item.id,
            content: item.content || item.caption || '',
            platforms: item.platform ? [item.platform] : [],
            scheduledAt: item.scheduled_date,
            status: item.status,
            caption: item.caption,
            hashtags: item.hashtags || [],
            title: item.title,
            media: [],
            createdAt: item.created_at,
          }));
          set({ posts });
        } catch (_) {}
      },

      createPost: async (postData) => {
        set({ isLoading: true });
        const { token } = useAuthStore.getState();
        try {
          const platforms = postData.platforms?.length ? postData.platforms : ['general'];
          const results = [];
          for (const platform of platforms) {
            const item = await apiFetch('/calendar', {
              method: 'POST',
              body: JSON.stringify({
                title: postData.content.substring(0, 80) || 'Post',
                content: postData.content,
                platform,
                status: postData.scheduledAt ? 'scheduled' : 'draft',
                scheduled_date: postData.scheduledAt || new Date().toISOString(),
                caption: postData.content,
                hashtags: postData.hashtags || [],
              }),
            }, token);
            results.push(item);
          }
          const newPost = {
            id: results[0]?.id || generateId(),
            ...postData,
            status: postData.scheduledAt ? 'scheduled' : 'draft',
            createdAt: new Date().toISOString(),
          };
          set(state => ({ posts: [newPost, ...state.posts], isLoading: false }));
          return { success: true, post: newPost };
        } catch (e) {
          set({ isLoading: false });
          return { success: false, error: e.message };
        }
      },

      updatePost: async (postId, updates) => {
        const { token } = useAuthStore.getState();
        try {
          await apiFetch(`/calendar/${postId}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
          }, token);
          set(state => ({
            posts: state.posts.map(p => p.id === postId ? { ...p, ...updates } : p),
          }));
          return { success: true };
        } catch (e) {
          return { success: false, error: e.message };
        }
      },

      deletePost: async (postId) => {
        const { token } = useAuthStore.getState();
        set({ isLoading: true });
        try {
          await apiFetch(`/calendar/${postId}`, { method: 'DELETE' }, token);
          set(state => ({ posts: state.posts.filter(p => p.id !== postId), isLoading: false }));
          return { success: true };
        } catch (e) {
          set({ isLoading: false });
          return { success: false, error: e.message };
        }
      },

      publishNow: async (postId) => {
        const { token } = useAuthStore.getState();
        try {
          await apiFetch(`/calendar/${postId}`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'published' }),
          }, token);
          set(state => ({
            posts: state.posts.map(p =>
              p.id === postId
                ? { ...p, status: 'published', publishedAt: new Date().toISOString() }
                : p
            ),
          }));
          return { success: true };
        } catch (e) {
          return { success: false, error: e.message };
        }
      },

      getScheduledPosts: () => get().posts.filter(p => p.status === 'scheduled'),
      getPublishedPosts: () => get().posts.filter(p => p.status === 'published'),
      getDraftPosts:     () => get().posts.filter(p => p.status === 'draft'),
    }),
    { name: 'flare-posts' }
  )
);

// ---------------------------------------------------------------------------
// Analytics Store — /quota for real data, chart placeholders ready for expansion
// ---------------------------------------------------------------------------
export const useAnalyticsStore = create((set) => ({
  overview: {
    totalPosts: 0,
    totalImpressions: 0,
    totalEngagements: 0,
    totalClicks: 0,
    followerGrowth: 0,
    engagementRate: 0,
  },
  platformStats: [],
  weeklyData: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(day => ({
    day, impressions: 0, engagements: 0, clicks: 0,
  })),
  bestTimes: [
    { day: 'Monday',    times: ['9:00 AM', '12:00 PM', '6:00 PM'] },
    { day: 'Tuesday',   times: ['10:00 AM', '2:00 PM', '7:00 PM'] },
    { day: 'Wednesday', times: ['9:00 AM', '1:00 PM', '5:00 PM'] },
    { day: 'Thursday',  times: ['11:00 AM', '3:00 PM', '8:00 PM'] },
    { day: 'Friday',    times: ['10:00 AM', '12:00 PM', '4:00 PM'] },
  ],

  refreshAnalytics: async () => {
    const { token } = useAuthStore.getState();
    if (!token) return { success: false };
    try {
      const quota = await apiFetch('/quota', {}, token);
      set({
        overview: {
          totalPosts: quota.usage?.publishes_this_month || 0,
          totalImpressions: 0,
          totalEngagements: 0,
          totalClicks: 0,
          followerGrowth: 0,
          engagementRate: 0,
        },
      });
      return { success: true };
    } catch (_) {
      return { success: false };
    }
  },
}));

// ---------------------------------------------------------------------------
// UI Store — theme, sidebar, toasts, modals (client-only)
// ---------------------------------------------------------------------------
export const useUIStore = create((set) => ({
  sidebarOpen: true,
  theme: 'light',
  toasts: [],
  modals: { createPost: false, connectPlatform: false, settings: false, upgrade: false },

  toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),

  setTheme: (theme) => {
    set({ theme });
    document.documentElement.classList.toggle('dark', theme === 'dark');
  },

  openModal:  (name) => set(state => ({ modals: { ...state.modals, [name]: true } })),
  closeModal: (name) => set(state => ({ modals: { ...state.modals, [name]: false } })),

  addToast: (toast) => {
    const id = generateId();
    set(state => ({ toasts: [...state.toasts, { ...toast, id }] }));
    setTimeout(
      () => set(state => ({ toasts: state.toasts.filter(t => t.id !== id) })),
      toast.duration || 5000
    );
  },

  removeToast: (id) => set(state => ({ toasts: state.toasts.filter(t => t.id !== id) })),
}));

// ---------------------------------------------------------------------------
// AI Caption Store — calls real Claude via FLARE /generate
// ---------------------------------------------------------------------------
export const useAIStore = create((set) => ({
  isGenerating: false,

  generateCaption: async (prompt, context = '') => {
    set({ isGenerating: true });
    const { token } = useAuthStore.getState();
    try {
      const fullPrompt = context
        ? `${context}\n\nTask: ${prompt}`
        : prompt || 'Generate a compelling social media caption for my brand';
      const data = await apiFetch('/generate', {
        method: 'POST',
        body: JSON.stringify({
          prompt: fullPrompt,
          system: 'You are a viral social media content strategist. Write engaging, platform-optimized captions.',
          max_tokens: 400,
        }),
      }, token);
      set({ isGenerating: false });
      return { success: true, content: data.content };
    } catch (e) {
      set({ isGenerating: false });
      return { success: false, error: e.message };
    }
  },
}));

// ---------------------------------------------------------------------------
// Team Store (Pro+ — local for now, no backend endpoint)
// ---------------------------------------------------------------------------
export const useTeamStore = create(
  persist(
    (set) => ({
      members: [
        {
          id: '1',
          name: 'Ajani',
          email: 'ajani@akaind.ca',
          role: 'owner',
          avatar: 'https://api.dicebear.com/7.x/initials/svg?seed=Ajani',
        },
      ],
      pendingInvites: [],

      inviteMember: async (email, role) => {
        // TODO: Wire to POST /team/invite when team backend is built
        set(state => ({
          pendingInvites: [
            ...state.pendingInvites,
            { id: generateId(), email, role, invitedAt: new Date().toISOString(), status: 'pending' },
          ],
        }));
        return { success: true };
      },

      removeMember: async (memberId) => {
        set(state => ({ members: state.members.filter(m => m.id !== memberId) }));
        return { success: true };
      },

      updateMemberRole: async (memberId, role) => {
        set(state => ({
          members: state.members.map(m => m.id === memberId ? { ...m, role } : m),
        }));
        return { success: true };
      },
    }),
    { name: 'flare-team' }
  )
);

// ---------------------------------------------------------------------------
// Subscription Store — syncs plan from /quota
// ---------------------------------------------------------------------------
export const useSubscriptionStore = create(
  persist(
    (set, get) => ({
      plan: 'free',
      billingCycle: 'monthly',
      features: {
        free:       { platforms: 2, scheduledPosts: 10,  analytics: 'basic',    teamMembers: 0,  aiCredits: 5 },
        starter:    { platforms: 4, scheduledPosts: 50,  analytics: 'standard', teamMembers: 0,  aiCredits: 50 },
        pro:        { platforms: 7, scheduledPosts: 200, analytics: 'advanced', teamMembers: 3,  aiCredits: 200 },
        business:   { platforms: 7, scheduledPosts: 500, analytics: 'advanced', teamMembers: 10, aiCredits: 500 },
        enterprise: { platforms: 7, scheduledPosts: -1,  analytics: 'advanced', teamMembers: -1, aiCredits: -1 },
      },

      syncFromAPI: async () => {
        const { token } = useAuthStore.getState();
        if (!token) return;
        try {
          const quota = await apiFetch('/quota', {}, token);
          set({ plan: quota.plan || 'free' });
        } catch (_) {}
      },

      upgradePlan: async (newPlan, cycle) => {
        set({ plan: newPlan, billingCycle: cycle || 'monthly' });
        return { success: true };
      },

      getCurrentFeatures: () => {
        const plan = get().plan;
        return get().features[plan] || get().features.free;
      },
    }),
    { name: 'flare-subscription' }
  )
);
